import { useMemo, useState } from 'react';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { RemoveCircleOutline } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useDeleteEntityContributor } from '@/hooks/use-delete-entity-contributor';
import { useDeleteContributorRoleSlotsByEventSlotMutation } from '@/state/mutations/contributors/use-delete-contributor-role-slots-by-event-slot';
import { useQueryClient } from '@tanstack/react-query';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';
import ConfirmationModal from '@/components/common/confirmation-modal';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';
import { EVENT_SLOT_TYPE } from '@/types/dtos/events/event-slot-content.enum';

const normalizeId = (value?: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ('id' in value && typeof (value as { id?: string }).id === 'string') {
      return (value as { id: string }).id;
    }
    if ('_id' in value && typeof (value as { _id?: string })._id === 'string') {
      return (value as { _id: string })._id;
    }
    if ('$oid' in value && typeof (value as { $oid?: string }).$oid === 'string') {
      return (value as { $oid: string }).$oid;
    }
  }
  return undefined;
};

export function resolveModeratorCanDeleteFlowRole({
  forceMasterMode,
  eventSlotIdParam,
  flowEntityId,
  flowForDelete,
  eventPlanSlots,
}: {
  forceMasterMode: boolean;
  eventSlotIdParam: string | undefined;
  flowEntityId: string;
  flowForDelete: FlowDto;
  eventPlanSlots?: { deleted?: boolean; contentType?: string; content?: unknown }[];
}): boolean {
  const flowSlotId = normalizeId(flowForDelete?.slot?.id);
  const flowSlotOriginId = normalizeId(flowForDelete?.slot?.origin);
  const hasFlowSlotContext = !!(flowSlotId || flowSlotOriginId);
  const isMasterMode = forceMasterMode || (!eventSlotIdParam && !hasFlowSlotContext);

  if (isMasterMode) {
    return true;
  }
  if (!eventPlanSlots) {
    return false;
  }

  const flowIds = new Set<string>(
    [normalizeId(flowEntityId), normalizeId(flowForDelete?.origin), normalizeId(flowForDelete?.id)].filter(
      Boolean,
    ) as string[],
  );
  const flowSlotCount = eventPlanSlots.filter((slot) => {
    if (slot.deleted === true) return false;
    if (slot.contentType !== EVENT_SLOT_TYPE.FLOW) return false;
    const slotContentId = normalizeId(slot.content);
    const slotContentOrigin =
      typeof slot.content === 'object' && slot.content
        ? normalizeId((slot.content as { origin?: unknown }).origin)
        : undefined;
    if (!slotContentId && !slotContentOrigin) return false;
    return (slotContentId && flowIds.has(slotContentId)) || (slotContentOrigin && flowIds.has(slotContentOrigin));
  }).length;

  return flowSlotCount <= 1;
}

export function moderatorDeleteMenuPresentation(canDeleteFlowRole: boolean): {
  deleteAction: 'delete' | 'removeFromSlot';
  deleteTarget: 'session' | 'flow';
} {
  return canDeleteFlowRole
    ? { deleteAction: 'delete', deleteTarget: 'flow' }
    : { deleteAction: 'removeFromSlot', deleteTarget: 'flow' };
}

type ModeratorDeleteHeaderButtonProps = {
  flowEntityId: string;
  flow: FlowDto;
  contributor: ContributorDto;
  roleId: string;
  eventSlotIdParam: string | undefined;
  forceMasterMode: boolean;
  /** When true, only render the confirmation modal (trigger lives elsewhere, e.g. overflow menu). */
  hideTrigger?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * Delete / remove-from-slot for flow moderators, shown on {@link CollapsibleBox} header (not in {@link Widget}).
 */
export function ModeratorDeleteHeaderButton({
  flowEntityId,
  flow,
  contributor,
  roleId,
  eventSlotIdParam,
  forceMasterMode,
  hideTrigger = false,
  open: controlledOpen,
  onOpenChange,
}: ModeratorDeleteHeaderButtonProps) {
  const t = useTranslations();
  const notification = useNotifications();
  const mode = useEventMode();
  const [internalOpen, setInternalOpen] = useState(false);
  const [isDeletePending, setIsDeletePending] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const eventPlanSlots = useEventStore((s) => s.eventPlan?.slots);
  const eventFlows = useEventStore((s) => s.eventFlows);
  const flowStoreFlow = useFlowStore((s) => s.flowPlan || s.flow);
  const deleteEntityContributor = useDeleteEntityContributor();
  const { mutateAsync: deleteRoleSlotsByEventSlot } = useDeleteContributorRoleSlotsByEventSlotMutation();
  const queryClient = useQueryClient();

  const flowForDelete = useMemo(() => {
    if (flowStoreFlow && (flowStoreFlow.id === flowEntityId || flowStoreFlow.origin === flowEntityId)) {
      return flowStoreFlow;
    }
    return eventFlows?.find((f) => f.id === flowEntityId || f.origin === flowEntityId) ?? flow;
  }, [flow, flowEntityId, eventFlows, flowStoreFlow]);

  const flowSlotId = normalizeId(flowForDelete?.slot?.id);
  const flowSlotOriginId = normalizeId(flowForDelete?.slot?.origin);

  const canDeleteFlowRole = resolveModeratorCanDeleteFlowRole({
    forceMasterMode,
    eventSlotIdParam,
    flowEntityId,
    flowForDelete,
    eventPlanSlots,
  });

  const displayName =
    contributor.profiles[0]?.displayName || contributor.email || t('event.detail.contributors.unknownContributor');

  const handleConfirm = async () => {
    setIsDeletePending(true);
    try {
      if (canDeleteFlowRole) {
        await deleteEntityContributor({
          contributor,
          entityType: 'Flow',
          entityId: flowEntityId,
        });
        await queryClient.refetchQueries({
          queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY],
          type: 'all',
        });
      } else {
        const eventSlotId = eventSlotIdParam || flowSlotId || flowSlotOriginId;
        if (!eventSlotId) {
          throw new Error('Missing event slot context for flow role removal.');
        }
        await deleteRoleSlotsByEventSlot({
          contributorId: contributor.id,
          roleId,
          eventSlotId,
        });
        await queryClient.refetchQueries({
          queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY],
          type: 'all',
        });
        notification.notifySuccess(
          t('event.detail.contributors.notifications.deleteRoleSlotSuccess', { name: displayName }),
        );
      }
      setOpen(false);
    } catch {
      if (!canDeleteFlowRole) {
        notification.notifyError(
          t('event.detail.contributors.notifications.deleteRoleSlotError', { name: displayName }),
        );
      }
    } finally {
      setIsDeletePending(false);
    }
  };

  const deleteDisabled = isOrganizeMode(mode);

  const deleteIconButton = (
    <IconButton
      size="small"
      color="default"
      disabled={deleteDisabled}
      data-testid="delete-Flow-contributor-button"
      onClick={(e) => {
        e.stopPropagation();
        setOpen(true);
      }}
      sx={{
        'color': 'action.active',
        '& > svg': {
          fontSize: '1.5rem',
          width: '1.5rem',
          height: '1.5rem',
        },
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      {canDeleteFlowRole ? <DeleteOutlined /> : <RemoveCircleOutline />}
    </IconButton>
  );

  return (
    <>
      {!hideTrigger ? (
        <Tooltip
          placement="left"
          disableInteractive
          title={
            canDeleteFlowRole
              ? t('flows.detail.contributors.deleteTitle', { name: displayName })
              : t('event.detail.contributors.deleteRoleSlot')
          }
        >
          {deleteDisabled ? <span>{deleteIconButton}</span> : deleteIconButton}
        </Tooltip>
      ) : null}
      <ConfirmationModal
        open={open}
        isLoading={isDeletePending}
        title={
          !canDeleteFlowRole
            ? t('event.detail.contributors.deleteRoleSlotModalTitle', { name: displayName })
            : t('flows.detail.contributors.deleteTitle', { name: displayName })
        }
        text={
          !canDeleteFlowRole
            ? t('event.detail.contributors.deleteRoleSlotModalText', { name: displayName })
            : t('flows.detail.contributors.deleteText', { name: displayName })
        }
        onConfirm={handleConfirm}
        onCancel={() => {
          if (isDeletePending) return;
          setOpen(false);
        }}
      />
    </>
  );
}
